import { useState, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import FitScreenIcon from '@mui/icons-material/FitScreen'
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic'

const UNDO_DURATION = 5000

const ADVISOR_CONFIG = {
  imageFit:  { label: 'Image Fit',  icon: FitScreenIcon,          color: '#5C6BC0', desc: 'Suggests a better image size (S/M/L) to match the available space in the layout, reducing white gaps or overflow.' },
  spaceFill: { label: 'Space Fill', icon: AutoAwesomeMosaicIcon,  color: '#26A69A', desc: 'Suggests switching layouts when the page has unused grid space, making better use of the available area.' },
}

function UndoButton({ undoId, onUndo, color }) {
  const [progress, setProgress] = useState(100)
  const startRef = useRef(Date.now())

  useEffect(() => {
    startRef.current = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startRef.current
      const remaining = Math.max(0, 100 - (elapsed / UNDO_DURATION) * 100)
      setProgress(remaining)
      if (remaining <= 0) clearInterval(interval)
    }, 50)
    return () => clearInterval(interval)
  }, [undoId])

  return (
    <span
      onClick={e => { e.stopPropagation(); onUndo?.(undoId) }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 4,
        background: color || '#686868', color: '#fff',
        fontSize: 10, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        cursor: 'pointer', userSelect: 'none',
      }}
    >
      <span style={{ position: 'relative', width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress variant="determinate" value={progress} size={14} thickness={5}
          sx={{ color: 'rgba(255,255,255,0.8)', position: 'absolute' }} />
      </span>
      Undo
    </span>
  )
}

function AdvisorChip({ advisor, active, stats, onToggle, onApplyAll, undoEntry, onUndo }) {
  const cfg = ADVISOR_CONFIG[advisor]
  if (!cfg) return null
  const Icon = cfg.icon
  const hasPending = stats.pending > 0

  return (
    <Tooltip title={cfg.desc} placement="bottom" arrow>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.7,
      }}>
        <Checkbox
          checked={active}
          onChange={onToggle}
          size="small"
          sx={{
            p: 0.3,
            color: cfg.color,
            '&.Mui-checked': { color: cfg.color },
          }}
        />
        <Icon sx={{ fontSize: 18, color: cfg.color }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: cfg.color }}>
          {cfg.label}
        </span>
        <span style={{ fontSize: 12, color: '#9e9e9e', marginRight: 2, minWidth: 14, textAlign: 'right' }}>
          {hasPending ? stats.pending : ''}
        </span>
        {undoEntry ? (
          <UndoButton undoId={undoEntry.id} onUndo={onUndo} color={cfg.color} />
        ) : (
          <span
            onClick={e => { e.stopPropagation(); if (active && hasPending) onApplyAll?.(advisor) }}
            style={{
              padding: '3px 10px', borderRadius: 3,
              background: active && hasPending ? cfg.color : '#e0e0e0',
              color: active && hasPending ? '#fff' : '#bdbdbd',
              fontSize: 11, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.04em',
              cursor: active && hasPending ? 'pointer' : 'default',
              userSelect: 'none',
              opacity: active && hasPending ? 1 : 0.45,
            }}
          >
            Apply all
          </span>
        )}
      </Box>
    </Tooltip>
  )
}

export function SuggestionBar({
  activeAdvisors = new Set(),
  onToggleAdvisor,
  imageFitStats  = { total: 0, pending: 0, accepted: 0, rejected: 0 },
  spaceFillStats = { total: 0, pending: 0, accepted: 0, rejected: 0 },
  onApplyAll,
  undoEntries = [],
  onUndo,
  availableAdvisors = [],
}) {
  if (availableAdvisors.length === 0) return null

  const hasImageFit  = availableAdvisors.includes('imageFit')
  const hasSpaceFill = availableAdvisors.includes('spaceFill')

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 1.5,
      py: 0.5,
      borderBottom: '1px solid #e0e0e0',
      background: '#fafafa',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Suggestions:
      </span>
      {hasSpaceFill && (
        <AdvisorChip
          advisor="spaceFill"
          active={activeAdvisors.has('spaceFill')}
          stats={spaceFillStats}
          onToggle={() => onToggleAdvisor?.('spaceFill')}
          onApplyAll={onApplyAll}
          undoEntry={undoEntries.find(e => e.advisor === 'spaceFill' && e.type === 'global')}
          onUndo={onUndo}
        />
      )}
      {hasSpaceFill && hasImageFit && (
        <span style={{ width: 1, height: 22, background: '#e0e0e0', flexShrink: 0 }} />
      )}
      {hasImageFit && (
        <AdvisorChip
          advisor="imageFit"
          active={activeAdvisors.has('imageFit')}
          stats={imageFitStats}
          onToggle={() => onToggleAdvisor?.('imageFit')}
          onApplyAll={onApplyAll}
          undoEntry={undoEntries.find(e => e.advisor === 'imageFit' && e.type === 'global')}
          onUndo={onUndo}
        />
      )}
    </Box>
  )
}
